import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Input, Typography, Avatar, Card, Space } from "antd";
import { ApolloError } from "@apollo/client";
import { useDebounce } from "@react-hook/debounce";
import { CloseOutlined, DeleteOutlined, UserOutlined } from "@ant-design/icons";
import Container from "@mui/material/Container";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";

import { useGetMentorshipProgramUsersByIdLazyQuery } from "../../graphql/queries/getMentorshipProgramUsersById.generated";
import { useGetMentorshipProgramLocationUsersByIdLazyQuery } from "../../graphql/queries/getMentorshipProgramLocationUserById.generated";
import { useInsertMentorshipsMutation } from "../../graphql/mutations/insertMentorships.generated";
import {
  GetMentorshipProgramByIdQuery,
  useGetMentorshipProgramByIdQuery,
} from "../../graphql/queries/getMentorshipProgramById.generated";
import { useAuth } from "../../context/AuthContext";
import { MatchingMethod } from "../../constants/input-values";
import routeNames from "../../constants/route-names";
import Colors from "../../styles/colors";
import "./mentorship-create-relationship.scss";

interface User {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email?: string;
  profilePhotoLink?: string | null;
}

interface MentorshipPair {
  id: string;
  mentor: User;
  mentee: User;
}

const MentorshipCreateRelationships = () => {
  const { loggedUserData } = useAuth();
  const params = useParams();
  const navigate = useNavigate();
  const programId = params.programId ? params.programId : null;

  const [searchMentor, setSearchMentor] = useState("");
  const [debouncedSearchMentor, setDebouncedSearchMentor] = useDebounce(
    searchMentor,
    300
  );

  useEffect(() => {
    setDebouncedSearchMentor(searchMentor);
  }, [searchMentor, setDebouncedSearchMentor]);

  const [searchMentee, setSearchMentee] = useState("");
  const [debouncedSearchMentee, setDebouncedSearchMentee] = useDebounce(
    searchMentee,
    300
  );

  useEffect(() => {
    setDebouncedSearchMentee(searchMentee);
  }, [searchMentee, setDebouncedSearchMentee]);

  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedMentor, setSelectedMentor] = useState<User | null>(null);
  const [selectedMentee, setSelectedMentee] = useState<User | null>(null);
  const [programMatchingMethod, setProgramMatchingMethod] =
    useState<string>("");
  const [mentorshipPairs, setMentorshipPairs] = useState<MentorshipPair[]>([]);
  const [currentSearchType, setCurrentSearchType] = useState<
    "mentor" | "mentee"
  >("mentor");
  const [saveLoading, setSaveLoading] = useState(false);
  const [hasWorkspaceLocation, setHasWorkspaceLocation] =
    useState<boolean>(false);

  const handleInsertMentorshipMutationError = useCallback(
    (error: ApolloError) => {
      console.log("Insert mutation error: ", error);
    },
    []
  );

  const [insertMentorship, { loading: mutationLoading }] =
    useInsertMentorshipsMutation({
      onError: handleInsertMentorshipMutationError,
    });

  const handleOnErrorGetMentorshipProgramUsers = useCallback(
    (error: ApolloError) => {
      console.log("Get MentorshipProgram Users ById error: ", error);
    },
    []
  );

  const handleOnErrorGetMentorshipProgramLocationUsers = useCallback(
    (error: ApolloError) => {
      console.log("Get MentorshipProgram Location Users ById error: ", error);
    },
    []
  );

  const [
    getMentorshipProgramUsers,
    { data: mentorSearchData, loading: searchLoading },
  ] = useGetMentorshipProgramUsersByIdLazyQuery({
    fetchPolicy: "network-only",
    errorPolicy: "all",
    nextFetchPolicy: "cache-first",
    onError: handleOnErrorGetMentorshipProgramUsers,
  });

  const [
    getMentorshipProgramLocationUsers,
    { data: mentorLocationSearchData, loading: searchLocationLoading },
  ] = useGetMentorshipProgramLocationUsersByIdLazyQuery({
    fetchPolicy: "network-only",
    errorPolicy: "all",
    nextFetchPolicy: "cache-first",
    onError: handleOnErrorGetMentorshipProgramLocationUsers,
  });

  const handleOnErrorsGetMentorshipProgramById = useCallback(
    (error: ApolloError) => {
      console.log("Get MentorshipProgram ById error: ", error);
    },
    []
  );

  const handleOnCompletedGetMentorshipProgramById = useCallback(
    (data: GetMentorshipProgramByIdQuery) => {
      const { matchingMethod, workspaceLocationId } =
        data.mentorshipProgram_by_pk || {};

      if (matchingMethod) {
        setProgramMatchingMethod(matchingMethod);

        if (matchingMethod === MatchingMethod.UserRequested) {
          navigate(-1);
        }
      }

      setHasWorkspaceLocation(!!workspaceLocationId);
    },
    [navigate]
  );

  const { data: mentorshipProgramData } = useGetMentorshipProgramByIdQuery({
    skip: !programId,
    variables: {
      id: Number(programId),
    },
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
    errorPolicy: "none",
    notifyOnNetworkStatusChange: true,
    onError: handleOnErrorsGetMentorshipProgramById,
    onCompleted: handleOnCompletedGetMentorshipProgramById,
  });

  // Determine which data source to use based on hasWorkspaceLocation
  const currentSearchData = useMemo(() => {
    const baseData = hasWorkspaceLocation
      ? mentorLocationSearchData
      : mentorSearchData;

    if (!baseData) {
      return baseData;
    }

    const updatedData = JSON.parse(JSON.stringify(baseData));

    let usersToFilter = [];
    const program = updatedData.mentorshipProgram?.[0];

    if (!program) {
      return baseData;
    }


    if (hasWorkspaceLocation && program.workspaceLocation) {
      usersToFilter = program.workspaceLocation.workspaceLocationUsers || [];

    } else if (!hasWorkspaceLocation && program.workspace) {
      const nestedUsers = program.workspace.workspaceUsers || [];
      usersToFilter = nestedUsers
        .map((wu: any) => wu.user)
        .filter((user: any) => user != null);
    } else {

      return baseData;
    }

    const filteredUsers = usersToFilter.filter((user: any) => {
      if (currentSearchType === "mentor") {
        return user.mentorEnabled === true;
      }
      if (currentSearchType === "mentee") {
        return user.menteeEnabled === true;
      }
      return true;
    });

    if (hasWorkspaceLocation && program.workspaceLocation) {
      program.workspaceLocation.workspaceLocationUsers = filteredUsers;
    } else if (!hasWorkspaceLocation && program.workspace) {
      program.workspace.workspaceUsers = filteredUsers.map((user: any) => ({
        user: user,
      }));
    }

    return updatedData;
  }, [
    hasWorkspaceLocation,
    mentorLocationSearchData,
    mentorSearchData,
    currentSearchType,
  ]);

  const currentSearchLoading = useMemo(() => {
    return hasWorkspaceLocation ? searchLocationLoading : searchLoading;
  }, []);
  const performSearch = useCallback(
    (searchTerm: string) => {
      if (!searchTerm.trim() || !programId) {
        setSearchResults([]);
        setShowDropdown(false);
        return;
      }

      const userFilterLocation = {
        _or: [
          { firstName: { _ilike: `%${searchTerm}%` } },
          { lastName: { _ilike: `%${searchTerm}%` } },
        ],
      };
      const userFilter = {
        user: {
          _or: [
            { firstName: { _ilike: `%${searchTerm}%` } },
            { lastName: { _ilike: `%${searchTerm}%` } },
          ],
        },
      };

      if (hasWorkspaceLocation) {
        getMentorshipProgramLocationUsers({
          variables: {
            programId: parseInt(programId),
            userWhere: userFilterLocation
          },
        });
      } else {
        getMentorshipProgramUsers({
          variables: {
            programId: parseInt(programId),
            userWhere: userFilter,
          },
        });
      }
    },
    [
      programId,
      hasWorkspaceLocation,
      getMentorshipProgramUsers,
      getMentorshipProgramLocationUsers,
    ]
  );

  useEffect(() => {
    if (currentSearchType === "mentor" && debouncedSearchMentor) {
      performSearch(debouncedSearchMentor);
    }
  }, [debouncedSearchMentor, currentSearchType, performSearch]);

  useEffect(() => {
    if (currentSearchType === "mentee" && debouncedSearchMentee) {
      performSearch(debouncedSearchMentee);
    }
  }, [debouncedSearchMentee, currentSearchType, performSearch]);

  useEffect(() => {
    if (!currentSearchData) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }

    let users: User[] = [];

    if (hasWorkspaceLocation) {

      const locationData = currentSearchData as any;
      if (
        locationData?.mentorshipProgram?.[0]?.workspaceLocation
          ?.workspaceLocationUsers
      ) {
        users =
          locationData.mentorshipProgram[0].workspaceLocation.workspaceLocationUsers.map(
            (locationUser: any) => ({
              id: locationUser.id,
              firstName: locationUser.firstName,
              lastName: locationUser.lastName,
              fullName: `${locationUser.firstName} ${locationUser.lastName}`,
              profilePhotoLink: locationUser.profilePhotoLink || null,
            })
          );
      }
    } else {
      const workspaceData = currentSearchData as any;
      if (workspaceData?.mentorshipProgram?.[0]?.workspace?.workspaceUsers) {
        users = workspaceData.mentorshipProgram[0].workspace.workspaceUsers.map(
          (wsUser: any) => ({
            id: wsUser.user.id,
            firstName: wsUser.user.firstName,
            lastName: wsUser.user.lastName,
            fullName: `${wsUser.user.firstName} ${wsUser.user.lastName}`,
            email: wsUser.user.email,
            profilePhotoLink: wsUser.user.profilePhotoLink || null,
          })
        );
      }
    }

    setSearchResults(users);
    setShowDropdown(users.length > 0);
  }, [currentSearchData, hasWorkspaceLocation]);

  const handleUserSelect = useCallback(
    (user: User, type: "mentor" | "mentee") => {
      if (type === "mentor") {
        setSelectedMentor(user);
        setSearchMentor(user.fullName);
      } else {
        setSelectedMentee(user);
        setSearchMentee(user.fullName);
      }
      setShowDropdown(false);
      setSearchResults([]);
    },
    []
  );

  const handleMentorInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchMentor(value);
      setSelectedMentor(null);
      setCurrentSearchType("mentor");

      if (!value.trim()) {
        setSearchResults([]);
        setShowDropdown(false);
      }
    },
    []
  );

  const handleMenteeInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchMentee(value);
      setSelectedMentee(null);
      setCurrentSearchType("mentee");

      if (!value.trim()) {
        setSearchResults([]);
        setShowDropdown(false);
      }
    },
    []
  );

  const handleInputFocus = useCallback(
    (type: "mentor" | "mentee") => {
      setCurrentSearchType(type);
      const searchText = type === "mentor" ? searchMentor : searchMentee;
      if (searchText.trim() && searchResults.length === 0) {
        performSearch(searchText);
      } else if (searchResults.length > 0) {
        setShowDropdown(true);
      }
    },
    [searchMentor, searchMentee, searchResults.length, performSearch]
  );

  const handleInputBlur = () => {
    setTimeout(() => setShowDropdown(false), 200);
  };

  const handleClearMentorSearch = () => {
    setSearchMentor("");
    setSelectedMentor(null);
    setSearchResults([]);
    setShowDropdown(false);
  };

  const handleClearMenteeSearch = () => {
    setSearchMentee("");
    setSelectedMentee(null);
    setSearchResults([]);
    setShowDropdown(false);
  };

  const handleAddPairing = useCallback(() => {
    if (selectedMentor && selectedMentee) {
      const newPair: MentorshipPair = {
        id: `${selectedMentor.id}-${selectedMentee.id}-${Date.now()}`,
        mentor: selectedMentor,
        mentee: selectedMentee,
      };

      setMentorshipPairs((prev) => [...prev, newPair]);

      setSelectedMentor(null);
      setSelectedMentee(null);
      setSearchMentor("");
      setSearchMentee("");
    }
  }, [selectedMentor, selectedMentee]);

  const handleRemovePairing = useCallback((pairId: string) => {
    setMentorshipPairs((prev) => prev.filter((pair) => pair.id !== pairId));
  }, []);

  const clearSavedMentorships = useCallback(() => {
    setMentorshipPairs([]);
  }, [programId]);

  const handleSaveMentorships = async () => {
    if (!programId || mentorshipPairs.length === 0) {
      return;
    }

    setSaveLoading(true);
    try {
      const objects = mentorshipPairs.map((pair) => ({
        mentorId: pair.mentor.id,
        menteeId: pair.mentee.id,
        mentorshipProgramId: parseInt(programId),
        workspaceId: loggedUserData?.workspaces[0].workspaceId,
        createdAt: new Date().toISOString(),
      }));

      const result = await insertMentorship({
        variables: {
          objects: objects,
        },
      });

      if (result.data) {
        setMentorshipPairs([]);
      }
    } catch (error) {
      console.error("Error saving mentorships:", error);
    } finally {
      const sectionKey = "relationships";

      const path = `${routeNames.MENTORSHIP_DETAILS.replace(
        ":programId",
        programId ?? ""
      )}?sectionKey=${sectionKey}`;

      navigate(path);
      setSaveLoading(false);
    }
  };

  const isAddButtonEnabled = useMemo(() => {
    return Boolean(selectedMentor && selectedMentee);
  }, [selectedMentee, selectedMentor]);

  const isSaveButtonEnabled = useMemo(() => {
    return mentorshipPairs.length > 0 && !mutationLoading;
  }, [mentorshipPairs.length, mutationLoading]);

  const avatarColor =
    Colors.Generic.gray79 || Colors.Generic?.highnoteBlue || "#1890ff";

  const isDisabled = false;

  const getMaxLabelWidth = useCallback(() => {
    const mentorLabel =
      mentorshipProgramData?.mentorshipProgram_by_pk?.mentorLabel ?? "Mentor";
    const menteeLabel =
      mentorshipProgramData?.mentorshipProgram_by_pk?.menteeLabel ?? "Mentee";

    const maxLength = Math.max(mentorLabel.length, menteeLabel.length);

    return Math.min(Math.max(maxLength * 8 + 20, 75), 200); // min 75px, max 200px
  }, [
    mentorshipProgramData?.mentorshipProgram_by_pk?.mentorLabel,
    mentorshipProgramData?.mentorshipProgram_by_pk?.menteeLabel,
  ]);

  const labelWidth = getMaxLabelWidth();

  return (
    <Container component="main" maxWidth={false}>
      <Typography.Title className="mcr-main-title" level={2}>
        Create New Relationships
      </Typography.Title>

      <div className="mcr-select-box">
        <Typography.Title level={5} className="mcr-select-text">
          1. Select one program participant for each role.
        </Typography.Title>

        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <div className="mcr-inner-select-box">
            <div className="mcr-profile-img-wrapper">
              <img
                src={
                  process.env.PUBLIC_URL + "/assets/images/mentor-filter.svg"
                }
                alt="mentor-profile-pic"
                className="mcr-mentorship-details-profile-img"
              />
            </div>
            <Typography.Text
              strong
              style={{ width: `${labelWidth}px`, minWidth: `${labelWidth}px` }}
            >
              {mentorshipProgramData?.mentorshipProgram_by_pk?.mentorLabel ??
                "Mentor"}
              :
            </Typography.Text>
            <div className="mcr-input-container">
              <Input
                placeholder="Search by name..."
                value={searchMentor}
                onChange={handleMentorInputChange}
                onFocus={() => handleInputFocus("mentor")}
                onBlur={handleInputBlur}
                style={{ width: "100%" }}
                prefix={
                  <img
                    src={`${process.env.PUBLIC_URL}/assets/images/searchbar-icon.svg`}
                    alt="Search"
                    className="mcr-search-icon"
                  />
                }
                suffix={
                  searchMentor && (
                    <CloseOutlined
                      className="mcr-clear-icon"
                      onClick={handleClearMentorSearch}
                      onMouseDown={(e) => e.preventDefault()}
                    />
                  )
                }
                disabled={isDisabled}
              />

              {showDropdown && currentSearchType === "mentor" && (
                <div className="mcr-dropdown">
                  {currentSearchLoading ? (
                    <div className="mcr-loading-container">
                      <CircularProgress size={20} />
                    </div>
                  ) : (
                    <>
                      {searchResults.map((user) => (
                        <div
                          key={user.id}
                          className="mcr-dropdown-item"
                          onMouseDown={() => handleUserSelect(user, "mentor")}
                        >
                          <Avatar
                            size="small"
                            style={{ backgroundColor: avatarColor }}
                            className="mcr-avatar"
                          >
                            {user.firstName?.[0]}
                            {user.lastName?.[0]}
                          </Avatar>
                          <Typography.Text>{user.fullName}</Typography.Text>
                        </div>
                      ))}

                      {searchResults.length === 0 && searchMentor.trim() && (
                        <div className="mcr-no-results-message">
                          No users found
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="mcr-inner-select-box">
            <div className="mcr-profile-img-wrapper">
              <img
                src={
                  process.env.PUBLIC_URL + "/assets/images/mentee-filter.svg"
                }
                alt="mentee-profile-pic"
                className="mcr-mentorship-details-profile-img"
              />
            </div>
            <Typography.Text
              strong
              style={{ width: `${labelWidth}px`, minWidth: `${labelWidth}px` }}
            >
              {mentorshipProgramData?.mentorshipProgram_by_pk?.menteeLabel ??
                "Mentee"}
              :
            </Typography.Text>
            <div className="mcr-input-container">
              <Input
                placeholder="Search by name..."
                value={searchMentee}
                onChange={handleMenteeInputChange}
                onFocus={() => handleInputFocus("mentee")}
                onBlur={handleInputBlur}
                style={{ width: "100%" }}
                prefix={
                  <img
                    src={`${process.env.PUBLIC_URL}/assets/images/searchbar-icon.svg`}
                    alt="Search"
                    className="mcr-search-icon"
                  />
                }
                suffix={
                  searchMentee && (
                    <CloseOutlined
                      className="mcr-clear-icon"
                      onClick={handleClearMenteeSearch}
                      onMouseDown={(e) => e.preventDefault()}
                    />
                  )
                }
                disabled={isDisabled}
              />

              {showDropdown && currentSearchType === "mentee" && (
                <div className="mcr-dropdown">
                  {currentSearchLoading ? (
                    <div className="mcr-loading-container">
                      <CircularProgress size={20} />
                    </div>
                  ) : (
                    <>
                      {searchResults.map((user) => (
                        <div
                          key={user.id}
                          className="mcr-dropdown-item"
                          onMouseDown={() => handleUserSelect(user, "mentee")}
                        >
                          <Avatar
                            size="small"
                            style={{ backgroundColor: avatarColor }}
                            className="mcr-avatar"
                          >
                            {user.firstName?.[0]}
                            {user.lastName?.[0]}
                          </Avatar>
                          <Typography.Text>{user.fullName}</Typography.Text>
                        </div>
                      ))}

                      {searchResults.length === 0 && searchMentee.trim() && (
                        <div className="mcr-no-results-message">
                          No users found
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="mcr-add-button-container">
            <Button
              variant="contained"
              onClick={handleAddPairing}
              disabled={!isAddButtonEnabled || isDisabled}
              className={`mcr-add-button ${
                !isAddButtonEnabled || isDisabled ? "disabled" : ""
              }`}
            >
              Add Match
            </Button>
          </div>
        </Space>
      </div>

      <div className="mcr-review-box">
        <Typography.Title level={5} className="mcr-select-text">
          2. Review New Relationships
        </Typography.Title>

        {mentorshipPairs.length === 0 ? (
          <Card>
            <Typography.Text type="secondary">
              No relationships added yet. Use the form above to create pairings.
            </Typography.Text>
          </Card>
        ) : (
          <Space direction="vertical" style={{ width: "100%" }} size="middle">
            <div className="mcr-table-header">
              <Typography.Text strong className="mcr-mentor-table-head">
                {mentorshipProgramData?.mentorshipProgram_by_pk?.mentorLabel ??
                  "Mentor"}
              </Typography.Text>
              <Typography.Text strong className="mcr-mentee-table-head">
                {mentorshipProgramData?.mentorshipProgram_by_pk?.menteeLabel ??
                  "Mentee"}
              </Typography.Text>
            </div>

            {mentorshipPairs.map((pair) => (
              <Card key={pair.id} size="small">
                <div className="mcr-review-table">
                  <div className="mcr-mentor-column">
                    <div className="mcr-mentor-photo-background">
                      {pair.mentor.profilePhotoLink ? (
                        <img
                          src={pair.mentor.profilePhotoLink}
                          alt={pair.mentor.fullName}
                          className="mcr-profile-photo"
                        />
                      ) : (
                        <UserOutlined
                          style={{ color: avatarColor }}
                          className="mcr-user-outlined"
                        />
                      )}
                    </div>
                    <Typography.Text strong>
                      {pair.mentor.fullName}
                    </Typography.Text>
                  </div>

                  <div className="mcr-mentee-column">
                    <div className="mcr-mentee-photo-background">
                      {pair.mentee.profilePhotoLink ? (
                        <img
                          src={pair.mentee.profilePhotoLink}
                          alt={pair.mentee.fullName}
                          className="mcr-profile-photo"
                        />
                      ) : (
                        <UserOutlined
                          style={{ color: avatarColor }}
                          className="mcr-user-outlined"
                        />
                      )}
                    </div>
                    <Typography.Text strong>
                      {pair.mentee.fullName}
                    </Typography.Text>
                  </div>

                  <DeleteOutlined
                    onClick={() => handleRemovePairing(pair.id)}
                    className="mcr-delete-icon"
                  />
                </div>
              </Card>
            ))}
          </Space>
        )}
      </div>

      <div className="mcr-actions-container">
        <div className="mcr-cancel-button-background">
          <Button
            variant="contained"
            onClick={clearSavedMentorships}
            disabled={!isSaveButtonEnabled}
            disableElevation
            className="mcr-cancel-button"
          >
            Cancel
          </Button>
        </div>
        <Button
          variant="contained"
          onClick={handleSaveMentorships}
          disabled={!isSaveButtonEnabled}
          className={`mcr-save-button ${
            !isSaveButtonEnabled || isDisabled ? "disabled" : ""
          }`}
        >
          Save
        </Button>
      </div>
    </Container>
  );
};

export default MentorshipCreateRelationships;
